import React, { FunctionComponent, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Alert,
  Box,
  ButtonBase,
  Collapse,
  Container,
  Grid,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import { useAppSelector } from '../../../hooks';
import {
  getFlexiblePlansState,
  IFlexiblePlan,
  getUserFlexiblePlans,
} from '../../../store/V2/FlexiblePlansSlice';
import {
  getSubscriptionPlansState,
  getUserSubscriptionPlans,
  ISubscriptionPlan,
} from '../../../store/V2/SubscriptionPlansSlice';
import FullScreenLoader from '../../../components/Loaders/FullScreenloader';
import TabBar, { ITabItem } from '../../../components/V2/Tabs/type1/TabBar';
import FlexiblePlanCard from './components/FlexiblePlanCard';
import FeatureCard, { IFeature } from './components/FeatureCard';
import SubscriptionPlanCard from './components/SubscriptionPlanCard';
import PlanPurchaseFooter from './components/PlanPurchaseFooter';

type TState = {
  selectedTab: ITabItem | null;
  selectedFlexiblePlan: IFlexiblePlan | null;
  selectedSubscriptionPlan: ISubscriptionPlan | null;
  tabs: ITabItem[];
};

const carouselButtonStyles: React.CSSProperties = {
  display: 'block',
  backgroundColor: '#01A79F',
  color: '#ffffff',
  right: '-24px',
  width: '30px',
  height: '30px',
  borderRadius: '100%',
  zIndex: 1,
};

const SampleNextArrow = (props) => {
  const { className, style, onClick } = props;

  if (className.includes('slick-disabled')) {
    return null;
  }

  return (
    <ButtonBase
      className={`${className} custom-slick-button`}
      onClick={onClick}
      style={{
        ...style,
        ...carouselButtonStyles,
      }}
    />
  );
};

const SamplePrevArrow = (props) => {
  const { className, style, onClick } = props;

  if (className.includes('slick-disabled')) {
    return null;
  }

  return (
    <ButtonBase
      className={`${className} custom-slick-button`}
      onClick={onClick}
      style={{
        ...style,
        ...carouselButtonStyles,
        content: 'unset !important',
      }}
    />
  );
};

const features: IFeature[] = [
  {
    imgName: 'quizzes-feature.png',
    bgColor: '#FDF5CA',
    title: 'Quizzes',
    description:
      'Test your knowledge with fun, interactive quizzes designed to challenge and help you master key topics!',
  },
  {
    imgName: 'revision-notes.png',
    bgColor: '#ECF6F8',
    title: 'Revision Notes',
    description:
      'Stay organized and on top of your studies with easy-to-follow revision notes that simplify complex concepts.',
  },
  {
    imgName: 'past-papers.png',
    bgColor: '#EEEEEE',
    title: 'Past Papers',
    description:
      'Practice with real exam papers from previous years to familiarize yourself with exam formats and boost your confidence.',
  },
  {
    imgName: 'ai-tutor.png',
    bgColor: '#D6F5D6',
    title: 'AI Tutor',
    description:
      'Get personalized support from our AI tutor, ready to guide you through tricky topics and offer smart study tips.',
  },
  {
    imgName: 'practice-questions.png',
    bgColor: '#FFEDCC',
    title: 'Practice Questions',
    description:
      'Sharpen your skills with practice questions tailored to help you ace your exams and understand core material.',
  },
  {
    imgName: 'exam-tips.png',
    bgColor: '#FFECE5',
    title: 'Exam Tips',
    description:
      'Gain valuable exam insights and strategies to help you manage time, stay focused, and succeed on exam day.',
  },
];

const carouselSettings = {
  dots: true,
  infinite: false,
  speed: 500,
  slidesToShow: 3,
  slidesToScroll: 3,
  accessibility: true,
  arrows: true,
  nextArrow: <SampleNextArrow />,
  prevArrow: <SamplePrevArrow />,
  responsive: [
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
    {
      breakpoint: 900,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 2,
      },
    },
  ],
};

const initialState: TState = {
  selectedTab: null,
  selectedFlexiblePlan: null,
  selectedSubscriptionPlan: null,
  tabs: [],
};

const BillingPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const location = useLocation();
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));

  // Store
  const subscriptionPlansState = useAppSelector(getSubscriptionPlansState);
  const flexiblePlansState = useAppSelector(getFlexiblePlansState);

  // State
  const [initialLoading, setInitialLoading] = useState(true);
  const [state, setState] = useState<TState>({ ...initialState });
  const [alertVisibility, setAlertVisibility] = useState(true);

  const questyStringParams = new URLSearchParams(location.search);
  const flexiblePlanList = flexiblePlansState.userFlexiblePlans || [];
  const subscriptionPlanList =
    subscriptionPlansState.userSubscriptionPlans || [];

  useEffect(() => {
    initiatePage();
  }, []);

  useEffect(() => {
    if (flexiblePlanList.length) {
      setState((prevState) => {
        const newStateData = { ...prevState };
        const newTabs = [...prevState.tabs];

        if (!prevState.tabs.some((item) => item.key === 'flexible')) {
          newTabs.push({
            index: prevState.tabs.length,
            key: 'flexible',
            title: 'Flexible Plans',
          });
        }

        if (!prevState.selectedTab && newTabs.length) {
          // eslint-disable-next-line prefer-destructuring
          newStateData.selectedTab = newTabs[0];
        }

        newStateData.tabs = [...newTabs];

        return newStateData;
      });
    }
  }, [flexiblePlanList]);

  useEffect(() => {
    if (subscriptionPlanList.length) {
      setState((prevState) => {
        const newStateData = { ...prevState };
        const newTabs = [...prevState.tabs];

        if (!prevState.tabs.some((item) => item.key === 'subscription')) {
          newTabs.push({
            index: prevState.tabs.length,
            key: 'subscription',
            title: 'Subscription Plans',
          });
        }

        if (!prevState.selectedTab && newTabs.length) {
          // eslint-disable-next-line prefer-destructuring
          newStateData.selectedTab = newTabs[0];
        }

        newStateData.tabs = [...newTabs];

        return newStateData;
      });
    }
  }, [subscriptionPlanList]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const initiatePage = async () => {
    await dispatch(getUserFlexiblePlans());
    await dispatch(getUserSubscriptionPlans());

    setInitialLoading(false);
  };

  const constructBreadcrumbOptions = () => {
    const options: Array<BreadcrumbOption> = [
      {
        type: 'link',
        title: 'Home',
        link: `${process.env.PUBLIC_URL}`,
      },
      {
        type: 'text',
        title: 'Plans & Billing',
        highlight: true,
      },
    ];

    return options;
  };

  const renderAlerts = () => {
    if (questyStringParams.get('paymentStatus') === 'canceled') {
      return (
        <Collapse in={alertVisibility}>
          <Stack direction="row" justifyContent="center">
            <Alert
              icon={false}
              color="warning"
              onClose={() => setAlertVisibility(false)}
              sx={{
                backgroundColor: 'warning.light',
                color: 'warning.dark',
                mb: '15px',
              }}
            >
              Payment Canceled!
            </Alert>
          </Stack>
        </Collapse>
      );
    }

    if (questyStringParams.get('paymentStatus') === 'success') {
      return (
        <Collapse in={alertVisibility}>
          <Stack direction="row" justifyContent="center">
            <Alert
              icon={false}
              color="success"
              onClose={() => setAlertVisibility(false)}
              sx={{
                backgroundColor: 'success.light',
                color: 'success.dark',
                mb: '15px',
              }}
            >
              Payment Successully Purchased!
            </Alert>
          </Stack>
        </Collapse>
      );
    }

    if (questyStringParams.get('paymentStatus') === 'failed') {
      return (
        <Collapse in={alertVisibility}>
          <Stack direction="row" justifyContent="center">
            <Alert
              icon={false}
              color="error"
              onClose={() => setAlertVisibility(false)}
              sx={{
                backgroundColor: 'error.light',
                color: 'error.dark',
                mb: '15px',
              }}
            >
              Payment Failed!
            </Alert>
          </Stack>
        </Collapse>
      );
    }

    return null;
  };

  const renderTabContent = () => {
    let content: JSX.Element | null = null;

    if (state.selectedTab) {
      switch (state.selectedTab.key) {
        case 'flexible': {
          const overRiddenCarouselSettings = {
            ...carouselSettings,
          };

          if (flexiblePlanList.length < 3) {
            overRiddenCarouselSettings.dots = false;
            overRiddenCarouselSettings.slidesToShow = flexiblePlanList.length;
            overRiddenCarouselSettings.slidesToScroll = 0;
          }

          content = (
            <Slider {...overRiddenCarouselSettings}>
              {flexiblePlanList.map((item, index) => {
                const isSelected = Boolean(
                  state.selectedFlexiblePlan &&
                    item._id === state.selectedFlexiblePlan._id
                );

                return (
                  <FlexiblePlanCard
                    key={`flexible-plan-card-${index}`}
                    plan={item}
                    selected={isSelected}
                    onClick={() =>
                      updateStateAttributes({
                        selectedFlexiblePlan: item,
                      })
                    }
                  />
                );
              })}
            </Slider>
          );

          break;
        }

        case 'subscription': {
          const overRiddenCarouselSettings = {
            ...carouselSettings,
          };

          if (subscriptionPlanList.length < 3) {
            overRiddenCarouselSettings.dots = false;
            overRiddenCarouselSettings.slidesToShow =
              subscriptionPlanList.length;
            overRiddenCarouselSettings.slidesToScroll = 0;
          }

          content = (
            <Slider {...overRiddenCarouselSettings}>
              {subscriptionPlanList.map((item, index) => {
                const isSelected = Boolean(
                  state.selectedSubscriptionPlan &&
                    item._id === state.selectedSubscriptionPlan._id
                );

                return (
                  <SubscriptionPlanCard
                    key={`flexible-plan-card-${index}`}
                    plan={item}
                    selected={isSelected}
                    onClick={() =>
                      updateStateAttributes({
                        selectedSubscriptionPlan: item,
                      })
                    }
                  />
                );
              })}
            </Slider>
          );

          break;
        }

        default:
          content = null;
          break;
      }
    }

    return content;
  };

  const breadcrumbOptions = constructBreadcrumbOptions();

  if (initialLoading) {
    return <FullScreenLoader />;
  }

  return (
    <div>
      <Container maxWidth="xl">
        <div style={{ padding: '30px 0' }}>
          <Breadcrumbs
            options={breadcrumbOptions}
            textColor="#647488"
            showDropDown={isXsScreen}
          />
        </div>
        {renderAlerts()}
        <Stack textAlign="center" mb="40px">
          <Typography
            fontWeight={800}
            fontSize="40px"
            sx={{
              background:
                'var(--Gradients, linear-gradient(104deg, #006C9C 7.37%, #002536 100%))',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Priced To Fit Your Needs
          </Typography>
          <Typography fontWeight={500} fontSize="16px" color="#647488">
            Tutopiya is excited to help you elevate your grades and become a
            learning superstar!
          </Typography>
        </Stack>
        {state.tabs.length > 1 ? (
          <Stack alignItems="center" mb="40px">
            <div style={{ width: '100%', maxWidth: '400px' }}>
              <TabBar
                tabsProps={{ variant: 'fullWidth' }}
                selectedTab={state.selectedTab || undefined}
                tabs={state.tabs}
                onChange={(e, value) =>
                  updateStateAttributes({
                    selectedTab: state.tabs[value],
                    selectedFlexiblePlan: null,
                    selectedSubscriptionPlan: null,
                  })
                }
              />
            </div>
          </Stack>
        ) : null}
        <Stack alignItems="center">
          <Box
            className="slider-container"
            sx={{
              width: '100%',
              maxWidth: '1280px',
              // '& .slick-list': { padding: '30px 15px' },
            }}
          >
            {renderTabContent()}
          </Box>
        </Stack>
        <Stack textAlign="center" mt="100px" mb="72px">
          <Typography fontWeight={800} fontSize="25px" color="#1E293B">
            Get Access to Even More Powerful Features!
          </Typography>
        </Stack>
        <Stack alignItems="center">
          <Grid container sx={{ maxWidth: '822px' }} rowSpacing="16px">
            {features.map((item, index) => (
              <Grid item xs={12} key={`feature-card-${index}`}>
                <FeatureCard feature={item} />
              </Grid>
            ))}
          </Grid>
        </Stack>
        {state.selectedFlexiblePlan || state.selectedSubscriptionPlan ? (
          <PlanPurchaseFooter
            flexiblePlan={state.selectedFlexiblePlan || undefined}
            subscriptionPlan={state.selectedSubscriptionPlan || undefined}
          />
        ) : null}
      </Container>
    </div>
  );
};

export default BillingPage;
